import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash'

import { categoryKey, separateCategoryStatusesAllowedEditing, separateCategoryStatusesLoadingWillAllowEditing } from '../../util/categoryConsts'
import { FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent } from '../styles/Containers'
import { ErrorMessage, WarningMessage } from '../common/UserMessage'
import Button, { IconButton, SecondaryButton } from '../common/Button'
import BodyText, { BoldFormTitle, BoldSpan, MedBodyText } from '../styles/Text'
import axios from '../../dataClient/dataClient'

import AutocompleteSelect from '../inputFields/AutocompleteSelect'

const FormTitle = styled(BoldFormTitle)`
  text-transform: none;
`

const schema = yup.object().shape({
  category: yup
    .object()
    .shape({
      id: yup.string(),
      label: yup.string().required('Label is required')
    })
    .typeError('Category is required')
    .required('Category is required')
})

function RestoreFileToCategoryButton({ fileId, name, categories = [], foiId, disabled = false, onSuccess = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [createdCategory, setCreatedCategory] = useState()

  const methods = useForm({ resolver: yupResolver(schema) })
  const { handleSubmit, reset } = methods

  const handleOpen = () => setIsVisible(true)
  const handleClose = () => {
    setIsVisible(false)
    reset()
    setError(false)
    setIsLoading(false)
    setCreatedCategory(undefined)
  }

  const onError = (errors, e) => console.error('Error', errors, e)

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    let categoryId = data.category.id ?? (createdCategory?.name === data.category.label ? createdCategory.id : undefined)
    if (!categoryId) {
      try {
        const { data: responseData } = await axios.post('/category', { name: data.category.label, foiId })
        categoryId = responseData.data.id
        setCreatedCategory({ name: data.category.label, id: categoryId })
      } catch (e) {
        console.error(`failed to create category with name `, data.category.label, e)
        setError(true)
        setIsLoading(false)
        return
      }
    }

    axios
      .post(`/category/${categoryId}/restoreFile`, { fileId })
      .then(() => {
        handleClose()
        onSuccess()
      })
      .catch(e => {
        console.error('Failed restoring file', fileId, e)
        setError(true)
        setIsLoading(false)
      })
  }

  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const restorableCategories = separateCategoryStatusesAllowedEditing.map(s => categoryKey[s].label).join(', ')
  const loadingCategories = separateCategoryStatusesLoadingWillAllowEditing.map(s => categoryKey[s].label).join(', ')

  return (
    <>
      <IconButton aria-label="Restore File" onClick={handleOpen} disabled={disabled}>
        <RestoreFromTrashIcon />
      </IconButton>
      <StyledDialog maxwidth="600px" open={isVisible} onClose={handleClose} keepMounted={false}>
        <StyledDialogContent>
          <FormProvider {...methods}>
            <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
              <FormTitle>Restore File: {name}</FormTitle>
              <BodyText>The restored file will be added to the selected category.</BodyText>
              <WarningMessage>
                <MedBodyText>
                  Files can only be restored to a category with one of the following statuses: <BoldSpan>{restorableCategories}</BoldSpan>. If the
                  category currently has one of the following loading statuses: <BoldSpan>{loadingCategories}</BoldSpan>, you can restore the file to
                  it once it has finished processing.
                </MedBodyText>
              </WarningMessage>
              <AutocompleteSelect id="category" label="Category" required options={categories} disabled={isLoading} />
              {error && <ErrorMessage>There was an error restoring the file. Please try again.</ErrorMessage>}
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <Button type="submit" loading={isLoading}>
                  Confirm
                </Button>
              </FlexEndContainer>
            </PaddedForm>
          </FormProvider>
        </StyledDialogContent>
      </StyledDialog>
    </>
  )
}

RestoreFileToCategoryButton.propTypes = {
  fileId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  categories: PropTypes.array,
  foiId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onSuccess: PropTypes.func
}

export default RestoreFileToCategoryButton
